import React, { useState, useRef } from 'react';

const SPRING = 'cubic-bezier(0.25, 1, 0.2, 1)';

// ─── Ikona checkmark ─────────────────────────────────────────────────────────
const CheckIcon: React.FC<{ opacity?: number }> = ({ opacity = 0.65 }) => (
  <svg width="34" height="34" viewBox="0 0 40 40" fill="none">
    <path
      d="M32 9.5L31.78 9.72L16.36 28.26L7.11 19L6.72 18.65C6.09 18.07 5.26 17.77 4.41 17.79C3.56 17.8 2.74 18.14 2.14 18.74C1.53 19.34 1.18 20.15 1.16 21C1.15 21.85 1.48 22.68 2.06 23.3L13.08 34.34C13.4 34.65 13.77 34.89 14.18 35.05C14.59 35.21 15.02 35.28 15.46 35.27C15.9 35.26 16.32 35.16 16.71 34.98C17.1 34.8 17.45 34.54 17.73 34.22L34.98 13.26C35.56 12.63 35.86 11.79 35.84 10.93C35.82 10.07 35.48 9.25 34.87 8.64C34.26 8.04 33.44 7.7 32.58 7.7C31.71 7.7 30.89 8.04 30.28 8.64Z"
      fill="white" fillOpacity={opacity}
    />
  </svg>
);

// ─── Ikona mikrofonu ──────────────────────────────────────────────────────────
const MicIcon: React.FC<{ opacity?: number; active?: boolean }> = ({ opacity = 0.55, active = false }) => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
    <rect x="9" y="2" width="6" height="11" rx="3" fill="white" fillOpacity={active ? 0.95 : opacity} />
    <path d="M5 10C5 13.87 8.13 17 12 17C15.87 17 19 13.87 19 10"
      stroke="white" strokeOpacity={active ? 0.75 : opacity * 0.6}
      strokeWidth="1.8" strokeLinecap="round" />
    <line x1="12" y1="17" x2="12" y2="21" stroke="white" strokeOpacity={active ? 0.75 : opacity * 0.6} strokeWidth="1.8" strokeLinecap="round" />
    <line x1="9" y1="21" x2="15" y2="21" stroke="white" strokeOpacity={active ? 0.75 : opacity * 0.6} strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// BottomActionBar — wspólny dolny pasek dla E2 i E3
//
// E2: confirm = następna karta / E3, mic tap = przejście E3, mic hold = panel ustaleń
// E3: confirm = wyślij + E1, mic hold = nagrywanie ustaleń, gear = settings
// ─────────────────────────────────────────────────────────────────────────────

interface BottomActionBarProps {
  // Confirm (środkowy przycisk — checkmark)
  onConfirmTap?: () => void;

  // Mic (prawy przycisk)
  onMicTap?: () => void;                     // krótkie kliknięcie
  onMicDown?: () => void;                    // natychmiastowy touchstart (gesture context — SR start na iOS)
  onMicHoldStart?: () => void;               // hold start → start dyktowania
  onMicHoldEnd?: () => void;                 // hold end → stop dyktowania

  // Panel ustaleń — wysuwa się z lewej gdy mic hold aktywny (E2)
  showArrangementsPanel?: boolean;
  arrangementsText?: string;
  onArrangementsChange?: (text: string) => void;
  isRecording?: boolean;
  arrangementCount?: number;
  arrangementsPanelContent?: React.ReactNode;
  actionBarBottomPct?: number;
}

export const BottomActionBar: React.FC<BottomActionBarProps> = ({
  onConfirmTap,
  onMicTap,
  onMicDown,
  onMicHoldStart,
  onMicHoldEnd,
  showArrangementsPanel = false,
  arrangementsText = '',
  onArrangementsChange,
  isRecording = false,
  arrangementCount = 0,
  arrangementsPanelContent,
  actionBarBottomPct = 0,
}) => {
  const [confirmPushed, setConfirmPushed] = useState(false);
  const [micPushed, setMicPushed]         = useState(false);

  const holdTimer   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdFired   = useRef(false);
  const tapEnabled  = useRef(true);

  // ── Mic handlers ──────────────────────────────────────────────────────────
  const handleMicDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setMicPushed(true);
    holdFired.current  = false;
    tapEnabled.current = true;
    onMicDown?.();  // natychmiastowy callback — SR start w gesture context (iOS Safari)
    holdTimer.current  = setTimeout(() => {
      holdFired.current  = true;
      tapEnabled.current = false;
      onMicHoldStart?.();
    }, 350);
  };

  const handleMicUp = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setMicPushed(false);
    if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; }
    if (holdFired.current) {
      onMicHoldEnd?.();
    } else if (tapEnabled.current) {
      onMicTap?.();
    }
    holdFired.current  = false;
    tapEnabled.current = true;
  };

  const handleMicLeave = () => {
    if (!micPushed) return;
    setMicPushed(false);
    if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; }
    if (holdFired.current) onMicHoldEnd?.();
    holdFired.current = false;
  };

  return (
    <>
      {/* ── Panel ustaleń (wysuwa się z lewej przy hold mic na E2) ── */}
      <div
        style={{
          position: 'fixed',
          left: showArrangementsPanel ? '4.8%' : '-62%',
          bottom: `calc(${actionBarBottomPct}vh + 75px + 16px)`,
          width: '56%',
          maxWidth: '400px',
          maxHeight: '280px', // ~4-5 kompaktowych kafelków
          background: 'linear-gradient(160deg, rgba(0,153,51,0.12) 0%, rgba(0,255,85,0.18) 55%, rgba(0,153,51,0.10) 100%)',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: '20px',
          backdropFilter: 'blur(22px)',
          WebkitBackdropFilter: 'blur(22px)',
          padding: '10px 12px',
          display: 'flex', flexDirection: 'column',
          transition: `left 420ms ${SPRING}`,
          zIndex: 200,
          boxShadow: '4px 0 32px rgba(0,0,0,0.35)',
          overflow: 'hidden', // Gradient mask zamiast scroll na całym panelu
        }}
      >
        {/* Nagłówek z linią oddzielającą */}
        <div style={{
          color: 'rgba(0,255,85,0.55)',
          fontSize: '9px', fontWeight: 400,
          fontFamily: '"Inter", sans-serif',
          letterSpacing: '3px', textTransform: 'uppercase',
          paddingBottom: '8px',
          marginBottom: '6px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}>
          Ustalenia {arrangementCount > 0 ? `(${arrangementCount})` : ''}
        </div>

        {/* Gradient mask na górze */}
        <div style={{
          position: 'absolute',
          top: '36px', // Poniżej nagłówka
          left: 0, right: 0,
          height: '16px',
          background: 'linear-gradient(180deg, rgba(0,0,0,0.2) 0%, transparent 100%)',
          pointerEvents: 'none',
          zIndex: 2,
        }} />

        {/* Gradient mask na dole */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0, right: 0,
          height: '24px',
          background: 'linear-gradient(0deg, rgba(0,0,0,0.3) 0%, transparent 100%)',
          pointerEvents: 'none',
          zIndex: 2,
        }} />

        {/* Scrollable content bez suwaków */}
        <div 
          className="arrangements-panel-scroll"
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            paddingTop: '4px',
            paddingBottom: '8px',
          }}
        >
          {arrangementsPanelContent ?? (
            <textarea
              value={arrangementsText}
              onChange={(e) => onArrangementsChange?.(e.target.value)}
              placeholder="Mów lub pisz ustalenia…"
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                resize: 'none',
                color: 'rgba(255,255,255,0.85)',
                fontSize: '14px',
                fontWeight: 300,
                fontFamily: '"Inter", sans-serif',
                lineHeight: 1.6,
                letterSpacing: '0.1px',
                minHeight: '80px',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
              spellCheck={false}
            />
          )}
        </div>
        <style>{`
          textarea::placeholder { color: rgba(255,255,255,0.2); }
          .arrangements-panel-scroll::-webkit-scrollbar { display: none; }
        `}</style>
      </div>

      {/* ── Główny pasek ──────────────────────────────────────────────────── */}
      <div
        className="relative select-none"
        style={{
          width: '320px',
          height: '75px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
      >
        {/* Kapsuła tło */}
        <div style={{
          position: 'absolute',
          left: 0, right: 0,
          top: '23px', bottom: '11px',
          borderRadius: '50px',
          border: '1px solid rgba(255,255,255,0.07)',
          background: 'rgba(0,0,0,0.01)',
          pointerEvents: 'none',
        }} />

        {/* ── Checkmark confirm (środek) ── */}
        <div
          onMouseDown={() => setConfirmPushed(true)}
          onMouseUp={() => { setConfirmPushed(false); onConfirmTap?.(); }}
          onMouseLeave={() => setConfirmPushed(false)}
          onTouchStart={(e) => { e.preventDefault(); setConfirmPushed(true); }}
          onTouchEnd={(e) => { e.preventDefault(); setConfirmPushed(false); onConfirmTap?.(); }}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            position: 'absolute',
            left: '122.5px',
            top: '0px',
            width: '75px', height: '75px',
            cursor: 'pointer',
            transform: confirmPushed ? 'scale(0.96)' : 'scale(1)',
            transition: `transform 200ms ${SPRING}`,
            touchAction: 'none',
          }}
        >
          <div style={{
            position: 'absolute', inset: 0,
            borderRadius: '50%',
            backdropFilter: 'blur(16.5px)',
            background: confirmPushed
              ? 'radial-gradient(50% 50% at 50% 50%, rgba(0,255,85,0.45) 47%, rgba(0,153,51,0.15) 100%)'
              : 'radial-gradient(50% 50% at 50% 50%, rgba(0,255,85,0.21) 44%, rgba(0,153,51,0.08) 87%)',
            boxShadow: confirmPushed
              ? 'inset 0 1px 3px rgba(255,255,255,0.6), 0 0 25px rgba(0,255,85,0.4)'
              : 'inset 0 1px 2px rgba(255,255,255,0.4), 0 8px 32px rgba(0,0,0,0.25)',
            border: confirmPushed ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.08)',
            transition: 'all 300ms ease',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <CheckIcon opacity={confirmPushed ? 0.95 : 0.65} />
          </div>
        </div>

        {/* ── Mic (prawy) — skala 90% względem kuli głównej ── */}
        <div
          onMouseDown={handleMicDown}
          onMouseUp={handleMicUp}
          onMouseLeave={handleMicLeave}
          onTouchStart={handleMicDown}
          onTouchEnd={handleMicUp}
          onTouchCancel={handleMicLeave}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            position: 'absolute',
            right: '8px',
            top: '5px',
            width: '67px', height: '67px',
            cursor: 'pointer',
            transform: micPushed ? 'scale(0.94)' : 'scale(1)',
            transition: `transform 200ms ${SPRING}`,
            touchAction: 'none',
          }}
        >
          <div style={{
            position: 'absolute', inset: 0,
            borderRadius: '50%',
            backdropFilter: 'blur(14px)',
            background: micPushed || isRecording
              ? 'radial-gradient(50% 50% at 50% 50%, rgba(0,255,85,0.38) 47%, rgba(0,153,51,0.12) 100%)'
              : 'radial-gradient(50% 50% at 50% 50%, rgba(0,255,85,0.12) 44%, rgba(0,153,51,0.05) 87%)',
            boxShadow: micPushed || isRecording
              ? 'inset 0 1px 3px rgba(255,255,255,0.5), 0 0 20px rgba(0,255,85,0.35)'
              : 'inset 0 1px 1px rgba(255,255,255,0.25), 0 6px 24px rgba(0,0,0,0.2)',
            border: micPushed ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(255,255,255,0.06)',
            transition: 'all 280ms ease',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: isRecording ? 'micGlowPulse 1.6s infinite ease-in-out' : 'none',
          }}>
            <MicIcon opacity={micPushed || isRecording ? 0.9 : 0.5} active={micPushed || isRecording} />
          </div>
        </div>

        <style>{`
          @keyframes micGlowPulse {
            0%,100% { filter: brightness(1) drop-shadow(0 0 6px rgba(0,255,85,0.3)); }
            50%      { filter: brightness(1.12) drop-shadow(0 0 18px rgba(0,255,85,0.7)); }
          }
        `}</style>
      </div>
    </>
  );
};
